import {
  getTestStandGroupNames,
  SETUP_TYPE,
  SUB_MODE,
  TS_PATHS,
} from 'features/plc/contracts/testStand.schema';
import { useHMIHandles } from 'features/plc/providers/HMIHandleProvider';
import { usePLCValue } from 'features/plc/providers/PLCProvider';
import saveTestStandLog from 'features/testStand/api/saveTestStandLog';
import updateTestStandLog from 'features/testStand/api/updateTestStandLog';
import useActiveTestLog from 'features/testStand/hooks/useActiveTestLog';
import getChannelInfo from 'features/testStand/utils/getChannelInfo';
import { serverTimestamp } from 'firebase/firestore';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const TestStandContext = createContext(null);

const EMPTY_CHANNEL_METADATA = { info: '' };
const IDLE_SAVE = { status: 'idle', error: '' };

function TestStandHandleGroupsSync() {
  const { groups, setGroups } = useHMIHandles();
  const setupType = usePLCValue(TS_PATHS.run.setupType) || SETUP_TYPE.CONTINUOUS_FLOW;
  const subMode = usePLCValue(TS_PATHS.run.subMode) || SUB_MODE.DISPLACEMENT;

  useEffect(() => {
    const nextGroups = getTestStandGroupNames(setupType, subMode);
    if (groups.length === nextGroups.length
        && groups.every((group, index) => group === nextGroups[index])) {
      return;
    }

    setGroups(nextGroups);
  }, [groups, setGroups, setupType, subMode]);

  return null;
}

function buildTestLogPayload(state, plcSnapshot, status) {
  const payload = {
    ...plcSnapshot,
    testNotes: state.testNotes,
    channels: {
      lipid: {
        info: state.channelMetadata.lipid.info,
        params: plcSnapshot?.channels?.lipid?.params || {},
      },
      aqueous: {
        info: state.channelMetadata.aqueous.info,
        params: plcSnapshot?.channels?.aqueous?.params || {},
      },
    },
  };
  if (status) payload.status = status;
  return payload;
}

function buildMetadataPatch(testNotes, channelMetadata) {
  return {
    testNotes,
    'channels.lipid.info': channelMetadata.lipid.info,
    'channels.aqueous.info': channelMetadata.aqueous.info,
  };
}

export function TestStandProvider({ children }) {
  const plcTestLogId = usePLCValue(TS_PATHS.run.testLogId) || '';
  const [testLogId, setTestLogId] = useState('');
  const [testNotes, setTestNotesState] = useState('');
  const [channelMetadata, setChannelMetadata] = useState({
    lipid: EMPTY_CHANNEL_METADATA,
    aqueous: EMPTY_CHANNEL_METADATA,
  });
  const [isMetadataDirty, setIsMetadataDirty] = useState(false);
  // Single save state shared across all save operations — they are never concurrent.
  const [saveState, setSaveState] = useState(IDLE_SAVE);
  const [expandedRunId, setExpandedRun] = useState(null);

  useEffect(() => {
    setTestLogId(plcTestLogId);
  }, [plcTestLogId]);

  const activeTestLogId = plcTestLogId || testLogId;
  const { data: activeTestLog, status: activeTestLogStatus } = useActiveTestLog(activeTestLogId);

  // Sync from Firestore whenever the snapshot changes, but only when the user
  // has no unsaved local edits — protects in-progress edits while still giving
  // real-time cross-tab sync when the form is clean.
  useEffect(() => {
    if (!activeTestLog || isMetadataDirty) return;
    setTestNotesState(activeTestLog.testNotes || '');
    setChannelMetadata({
      lipid: { info: getChannelInfo(activeTestLog.channels?.lipid) },
      aqueous: { info: getChannelInfo(activeTestLog.channels?.aqueous) },
    });
  }, [activeTestLog, isMetadataDirty]);

  useEffect(() => {
    if (activeTestLogStatus === 'error') {
      setSaveState({ status: 'error', error: 'Failed to listen to test log' });
    }
  }, [activeTestLogStatus]);

  const setTestNotes = useCallback((value) => {
    setTestNotesState(value);
    setIsMetadataDirty(true);
  }, []);

  const setChannelMetadataField = useCallback((channel, key, value) => {
    setChannelMetadata((current) => ({
      ...current,
      [channel]: { ...current[channel], [key]: value },
    }));
    setIsMetadataDirty(true);
  }, []);

  const setLipidMetadata = useCallback((key, value) => {
    setChannelMetadataField('lipid', key, value);
  }, [setChannelMetadataField]);

  const setAqueousMetadata = useCallback((key, value) => {
    setChannelMetadataField('aqueous', key, value);
  }, [setChannelMetadataField]);

  const saveMetadata = useCallback(async () => {
    if (!activeTestLogId) return;
    setSaveState({ status: 'saving', error: '' });
    try {
      await updateTestStandLog(activeTestLogId, buildMetadataPatch(testNotes, channelMetadata));
      setIsMetadataDirty(false);
      setSaveState({ status: 'saved', error: '' });
    } catch (err) {
      setSaveState({ status: 'error', error: err.message || 'Failed to save' });
    }
  }, [activeTestLogId, channelMetadata, testNotes]);

  const saveTestLog = useCallback(async (status, plcSnapshot = {}, options = {}) => {
    setSaveState({ status: 'saving', error: '' });
    try {
      const saveTargetId = options.forceNew ? '' : (plcTestLogId || testLogId);
      const isNew = !saveTargetId;
      const payload = buildTestLogPayload({ testNotes, channelMetadata }, plcSnapshot, status);
      if (isNew) payload.status = status || 'draft';

      const savedId = await saveTestStandLog({ activeTestLogId: saveTargetId, payload, isNew });

      if (testLogId !== savedId) setTestLogId(savedId);
      setIsMetadataDirty(false);
      setSaveState({ status: 'saved', error: '' });
      return savedId;
    } catch (err) {
      setSaveState({ status: 'error', error: err.message || 'Failed to save test log' });
      throw err;
    }
  }, [channelMetadata, plcTestLogId, testLogId, testNotes]);

  const markRunStopped = useCallback(async (status) => {
    if (!activeTestLogId) return;
    try {
      await updateTestStandLog(activeTestLogId, {
        status,
        stoppedAt: serverTimestamp(),
      });
    } catch {
      // best-effort, don't surface to user
    }
  }, [activeTestLogId]);

  const resetTestMetadata = useCallback(() => {
    setTestLogId('');
    setTestNotesState('');
    setChannelMetadata({ lipid: EMPTY_CHANNEL_METADATA, aqueous: EMPTY_CHANNEL_METADATA });
    setIsMetadataDirty(false);
    setSaveState(IDLE_SAVE);
  }, []);

  const state = useMemo(() => ({
    testLogId,
    testNotes,
    isMetadataDirty,
    saveState,
    activeTestLog,
    activeTestLogStatus,
    channelMetadata,
    expandedRunId,
  }), [
    testLogId,
    testNotes,
    isMetadataDirty,
    saveState,
    activeTestLog,
    activeTestLogStatus,
    channelMetadata,
    expandedRunId,
  ]);

  const value = useMemo(() => ({
    state,
    setTestNotes,
    setTestLogId,
    setLipidMetadata,
    setAqueousMetadata,
    setExpandedRun,
    saveMetadata,
    saveTestLog,
    markRunStopped,
    resetTestMetadata,
  }), [
    state,
    setTestNotes,
    setLipidMetadata,
    setAqueousMetadata,
    saveMetadata,
    saveTestLog,
    markRunStopped,
    resetTestMetadata,
  ]);

  return (
    <TestStandContext.Provider value={value}>
      <TestStandHandleGroupsSync />
      {children}
    </TestStandContext.Provider>
  );
}
TestStandProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTestStand() {
  const ctx = useContext(TestStandContext);
  if (!ctx) throw new Error('useTestStand must be used within TestStandProvider');
  return ctx;
}
